<template>
  <a-config-provider :locale="antLocale">
    <div class="page">
      <!-- 顶部返回栏 -->
      <div class="title-bar">
        <a-icon type="arrow-left" @click="goBack" class="back-icon" />
        <span class="title">{{ dramaTitle }}</span>
      </div>

      <!-- 可拖动的表格容器 -->
      <div
          class="table-container"
          ref="tableContainer"
          @mousedown="onDragStart"
          @mousemove="onDragMove"
          @mouseup="onDragEnd"
          @mouseleave="onDragEnd"
          @touchstart.passive="onTouchStart"
          @touchmove.passive="onTouchMove"
          @touchend.passive="onTouchEnd"
      >
        <a-table
            :columns="tableColumns"
            :data-source="data"
            :pagination="paginationConfig"
            :loading="loading"
            rowKey="episodeNo"
            @change="handleTableChange"
            :scroll="{ x: totalWidth, y: 520 }"
            :rowClassName="rowClassName"
            bordered
        >
          <!-- 是否付费 -->
          <template slot="paid" slot-scope="text">
            <a-tag :color="Number(text) === 1 ? 'orange' : 'green'">
              {{ Number(text) === 1 ? t('paid') : t('free') }}
            </a-tag>
          </template>

          <!-- 集标题 -->
          <template slot="episodeTitle" slot-scope="text">
            <a-tooltip :title="text">
              <span class="col-ellipsis">{{ text }}</span>
            </a-tooltip>
          </template>

          <!-- 上线时间 -->
          <template slot="onlineTime" slot-scope="text">
            <span>{{ text || '-' }}</span>
          </template>

          <!-- 数值类 -->
          <template slot="viewCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="uniqueViewerCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="favoritesCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="commentCountDanmu" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="shareCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="quitUsersCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="returnUsersCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="paidUsersThisEpisode" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="subsequentPaidContributorsCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="lastFreeEpisodeUserCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>
          <template slot="adViewersCount" slot-scope="text">
            <span>{{ formatNumber(text) }}</span>
          </template>

          <!-- 平均观看时长 -->
          <template slot="avgWatchDurationSeconds" slot-scope="text">
            <span>{{ formatDuration(text) }}</span>
          </template>

          <!-- 百分比类 -->
          <template slot="completionRate" slot-scope="text">
            <span>{{ formatPercent(text) }}</span>
          </template>
          <template slot="effectiveCompletionRate" slot-scope="text">
            <span>{{ formatPercent(text) }}</span>
          </template>
          <template slot="favoritesRate" slot-scope="text">
            <span>{{ formatPercent(text) }}</span>
          </template>
          <template slot="commentRateDanmu" slot-scope="text">
            <span>{{ formatPercent(text) }}</span>
          </template>
          <template slot="shareRate" slot-scope="text">
            <span>{{ formatPercent(text) }}</span>
          </template>
          <template slot="nextEpisodeRetentionRate" slot-scope="text">
            <span>{{ formatPercent(text) }}</span>
          </template>

          <!-- 金额类 -->
          <template slot="paidRevenueThisEpisode" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>
          <template slot="filmRevenue" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>
          <template slot="adRevenueThisEpisode" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>
          <template slot="arppu" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>

          <!-- 片商专属：观看/广告/总收入 -->
          <template slot="viewRevenue" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>
          <template slot="adRevenue" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>
          <template slot="viewAndAdRevenue" slot-scope="text">
            <span>{{ formatCurrency(text) }}</span>
          </template>
        </a-table>
      </div>
    </div>
  </a-config-provider>
</template>

<script>
import request from '@/utils/request'
import { mapGetters } from 'vuex'

// ✅ AntDV 内置分页/表格文案：按角色切换中英文
// 如果你项目用 es 路径不报错，也可以换成：ant-design-vue/es/locale/zh_CN & en_US
import zhCN from 'ant-design-vue/lib/locale-provider/zh_CN'
import enUS from 'ant-design-vue/lib/locale-provider/en_US'

// ✅ 覆写分页“Go to” -> “Jump to”
const EN_US_CUSTOM = {
  ...enUS,
  Pagination: {
    ...(enUS && enUS.Pagination ? enUS.Pagination : {}),
    jump_to: 'Jump to',
  }
}

// ✅ 文案：非片商中文不变；片商英文
const TEXT = {
  zh: {
    dramaDetails: '短剧详情',
    paid: '付费',
    free: '免费',
    total: total => `共 ${total} 条`,
    loadFail: '获取单集数据失败',
  },
  en: {
    dramaDetails: 'Drama Details',
    paid: 'Paid',
    free: 'Free',
    total: total => `Total ${total}`,
    loadFail: 'Failed to load episode data',
  }
}

// ✅ 列标题翻译（按 dataIndex 映射）
const COL_TITLE = {
  en: {
    episodeNo: 'Episode No.',
    episodeTitle: 'Episode Title',
    paid: 'Paid?',
    onlineTime: 'Release Time',
    viewCount: 'Views',
    uniqueViewerCount: 'Viewers',
    avgWatchDurationSeconds: 'Avg Watch Time',
    completionRate: 'Completion Rate',
    effectiveCompletionRate: 'Effective Completion Rate',
    favoritesCount: 'Favorites',
    favoritesRate: 'Favorites Rate',
    commentCountDanmu: 'Comments (Danmu)',
    commentRateDanmu: 'Comment Rate (Danmu)',
    shareCount: 'Shares',
    shareRate: 'Share Rate',
    nextEpisodeRetentionRate: 'Next-episode Retention',
    quitUsersCount: 'Churned Users',
    returnUsersCount: 'Returning Users',
    paidUsersThisEpisode: 'Paying Users (This Episode)',
    paidRevenueThisEpisode: 'Paid Revenue (RM)',
    filmRevenue: 'Film Partner Revenue (RM)',
    subsequentPaidContributorsCount: 'Subsequent Paying Contributors',
    arppu: 'ARPPU (RM)',
    lastFreeEpisodeUserCount: 'Users (First Pay / Last Free)',
    adViewersCount: 'Ad Viewers (This Episode)',
    adRevenueThisEpisode: 'Ad Revenue (This Episode) (RM)',
    viewRevenue: 'View Revenue (RM)',
    adRevenue: 'Ad Revenue (RM)',
    viewAndAdRevenue: 'Total Earnings (RM)',
  }
}

export default {
  name: 'EpisodeStats',
  data() {
    const cols = [
      { title: '集数编号', dataIndex: 'episodeNo', width: 100 },
      { title: '集标题', dataIndex: 'episodeTitle', width: 240, scopedSlots: { customRender: 'episodeTitle' } },
      { title: '是否付费', dataIndex: 'paid', width: 100, scopedSlots: { customRender: 'paid' } },
      { title: '上线时间', dataIndex: 'onlineTime', width: 160, scopedSlots: { customRender: 'onlineTime' } },
      { title: '观看次数(播放总次数)', dataIndex: 'viewCount', width: 120, scopedSlots: { customRender: 'viewCount' } },
      { title: '观看用户数(去重)', dataIndex: 'uniqueViewerCount', width: 120, scopedSlots: { customRender: 'uniqueViewerCount' } },
      { title: '平均观看时长', dataIndex: 'avgWatchDurationSeconds', width: 120, scopedSlots: { customRender: 'avgWatchDurationSeconds' } },
      { title: '完播率', dataIndex: 'completionRate', width: 100, scopedSlots: { customRender: 'completionRate' } },
      { title: '有效完播率', dataIndex: 'effectiveCompletionRate', width: 120, scopedSlots: { customRender: 'effectiveCompletionRate' } },
      { title: '收藏数', dataIndex: 'favoritesCount', width: 100, scopedSlots: { customRender: 'favoritesCount' } },
      { title: '收藏率', dataIndex: 'favoritesRate', width: 100, scopedSlots: { customRender: 'favoritesRate' } },
      { title: '评论数(弹幕)', dataIndex: 'commentCountDanmu', width: 110, scopedSlots: { customRender: 'commentCountDanmu' } },
      { title: '评论率(弹幕)', dataIndex: 'commentRateDanmu', width: 110, scopedSlots: { customRender: 'commentRateDanmu' } },
      { title: '分享数', dataIndex: 'shareCount', width: 100, scopedSlots: { customRender: 'shareCount' } },
      { title: '分享率', dataIndex: 'shareRate', width: 100, scopedSlots: { customRender: 'shareRate' } },
      { title: '下级留存率', dataIndex: 'nextEpisodeRetentionRate', width: 120, scopedSlots: { customRender: 'nextEpisodeRetentionRate' } },
      { title: '弃剧用户数', dataIndex: 'quitUsersCount', width: 120, scopedSlots: { customRender: 'quitUsersCount' } },
      { title: '回流用户数', dataIndex: 'returnUsersCount', width: 120, scopedSlots: { customRender: 'returnUsersCount' } },
      { title: '在本集付费的用户数', dataIndex: 'paidUsersThisEpisode', width: 150, scopedSlots: { customRender: 'paidUsersThisEpisode' } },
      { title: '本集付费总收入(元)', dataIndex: 'paidRevenueThisEpisode', width: 150, scopedSlots: { customRender: 'paidRevenueThisEpisode' } },
      { title: '片商收益(元)', dataIndex: 'filmRevenue', width: 150, scopedSlots: { customRender: 'filmRevenue' } },
      { title: '后续付费贡献人数', dataIndex: 'subsequentPaidContributorsCount', width: 150, scopedSlots: { customRender: 'subsequentPaidContributorsCount' } },
      { title: '单集 ARPPU(元)', dataIndex: 'arppu', width: 120, scopedSlots: { customRender: 'arppu' } },
      { title: '作为首付/最后免费集的用户数', dataIndex: 'lastFreeEpisodeUserCount', width: 200, scopedSlots: { customRender: 'lastFreeEpisodeUserCount' } },
      { title: '本集看广告的用户数', dataIndex: 'adViewersCount', width: 150, scopedSlots: { customRender: 'adViewersCount' } },
      { title: '本集广告总收入(元)', dataIndex: 'adRevenueThisEpisode', width: 150, scopedSlots: { customRender: 'adRevenueThisEpisode' } },
      { title: '观看收入(元)', dataIndex: 'viewRevenue', width: 150, scopedSlots: { customRender: 'viewRevenue' } },
      { title: '广告收入(元)', dataIndex: 'adRevenue', width: 150, scopedSlots: { customRender: 'adRevenue' } },
      { title: '总收入(元)', dataIndex: 'viewAndAdRevenue', width: 150, scopedSlots: { customRender: 'viewAndAdRevenue' } }
    ]

    return {
      loading: false,
      dramaTitle: '短剧详情',
      data: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showQuickJumper: true,
      },
      columns: cols,

      // 拖拽状态
      isDragging: false,
      dragStartX: 0,
      scrollStartLeft: 0,
    }
  },

  computed: {
    ...mapGetters(['roles']),

    // ✅ 是否片商角色（根据你实际角色编码调整）
    isFilmMerchant() {
      const rs = this.roles || []
      return rs.includes('film_user')
    },

    // ✅ 片商全英文，非片商中文
    lang() {
      return this.isFilmMerchant ? 'en' : 'zh'
    },

    // ✅ AntDV 内置文案（分页/表格）按语言切换：解决“底部分页导航英文”
    antLocale() {
      return this.isFilmMerchant ? EN_US_CUSTOM : zhCN
    },

    // ✅ 分页文案按语言动态（showTotal 你自己控制）
    paginationConfig() {
      return {
        ...this.pagination,
        showTotal: total => (TEXT[this.lang] || TEXT.zh).total(total),
      }
    },

    // ✅ 列：先翻译（仅片商），再按角色隐藏
    tableColumns() {
      const translated = this.columns.map(col => {
        if (this.lang !== 'en') return col
        const titleMap = COL_TITLE.en || {}
        const newTitle = titleMap[col.dataIndex] || col.title
        return { ...col, title: newTitle }
      })

      // ✅ 片商角色需要隐藏的列
      const hiddenForFilmMerchant = [
        'paidRevenueThisEpisode',
        'filmRevenue',
        'totalAdRevenue', // 兜底
        'arppu',
        'adRevenueThisEpisode',
        'paidUsersThisEpisode',
        'subsequentPaidContributorsCount',
        'lastFreeEpisodeUserCount',
        'paid',
        'onlineTime',
        'avgWatchDurationSeconds',
        'completionRate',
        'effectiveCompletionRate',
        'nextEpisodeRetentionRate',
        'quitUsersCount',
        'returnUsersCount',
        'favoritesRate',
        'commentCountDanmu',
        'commentRateDanmu',
        'shareRate',
        'viewRevenue',
        'adViewersCount',
        'adRevenueThisEpisode',
        'adRevenue'
      ]

      // ✅ 非片商隐藏片商专属列
      const hiddenForNonFilm = [
        'viewRevenue',
        'viewAndAdRevenue'
      ]

      if (this.isFilmMerchant) {
        return translated.filter(col => !hiddenForFilmMerchant.includes(col.dataIndex))
      }
      return translated.filter(col => !hiddenForNonFilm.includes(col.dataIndex))
    },

    // 根据当前可见列动态计算总宽度
    totalWidth() {
      return this.tableColumns.reduce((s, c) => s + (c.width || 120), 0)
    },
  },

  methods: {
    // ✅ 统一取文案
    t(key) {
      const pack = TEXT[this.lang] || TEXT.zh
      return pack[key] || key
    },

    // ✅ 更新标题：片商英文、非片商中文
    updateDramaTitle() {
      const { title, videoId } = this.$route.query
      const base = this.t('dramaDetails')
      this.dramaTitle = videoId ? `${videoId} / ${title || ''}` : (title || base)
    },

    async fetchData() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.current,
          pageSize: this.pagination.pageSize,
          videoId: this.$route.query.videoId,
        }
        const res = await request.get('/data/aggregation/gather/page', { params })
        const { records = [], total = 0 } = (res && res.data) || {}
        this.data = records
        this.pagination.total = total
      } catch (e) {
        this.$message.error(this.t('loadFail'))
        // eslint-disable-next-line no-console
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.fetchData()
    },

    goBack() {
      this.$router.back()
    },

    rowClassName(record, index) {
      return index % 2 === 0 ? 'row-even' : ''
    },

    /* ---------- 格式化 ---------- */
    formatDuration(v) {
      if (v == null || v === '') return '-'
      const num = Number(v)
      if (isNaN(num)) return v
      if (num < 60) return num.toFixed(0) + 's'
      const m = Math.floor(num / 60)
      const s = Math.floor(num % 60)
      return `${m}:${s < 10 ? '0' + s : s}`
    },
    formatNumber(v) {
      if (v == null || v === '') return '-'
      return v
    },
    formatPercent(v) {
      if (v == null || v === '') return '-'
      const num = Number(v)
      return isNaN(num) ? v : (num * 100).toFixed(2) + '%'
    },
    formatCurrency(v) {
      if (v == null || v === '') return '-'
      const str = String(v).trim()
      if (/^RM\s*/i.test(str)) return str
      const num = Number(v)
      return isNaN(num) ? v : `RM ${num.toFixed(2)}`
    },

    /* ---------- 拖动滚动 ---------- */
    onDragStart(e) {
      if (e.button !== 0) return
      this.isDragging = true
      this.dragStartX = e.clientX
      this.scrollStartLeft = this.$refs.tableContainer.scrollLeft
      this.$refs.tableContainer.classList.add('dragging')
    },
    onDragMove(e) {
      if (!this.isDragging) return
      const dx = e.clientX - this.dragStartX
      this.$refs.tableContainer.scrollLeft = this.scrollStartLeft - dx
    },
    onDragEnd() {
      if (!this.isDragging) return
      this.isDragging = false
      this.$refs.tableContainer.classList.remove('dragging')
    },
    onTouchStart(e) {
      if (!e.touches || !e.touches.length) return
      this.isDragging = true
      this.dragStartX = e.touches[0].clientX
      this.scrollStartLeft = this.$refs.tableContainer.scrollLeft
    },
    onTouchMove(e) {
      if (!this.isDragging) return
      const dx = e.touches[0].clientX - this.dragStartX
      this.$refs.tableContainer.scrollLeft = this.scrollStartLeft - dx
    },
    onTouchEnd() {
      this.isDragging = false
    },
  },

  mounted() {
    this.updateDramaTitle()
    this.fetchData()
  },

  watch: {
    '$route.query'() {
      this.updateDramaTitle()
    },
    // ✅ roles 异步回来后，lang 会变化，标题同步刷新
    lang() {
      this.updateDramaTitle()
    }
  },
}
</script>

<style scoped>
.page {
  padding: 16px;
  background: #ffffff;
}

.title-bar {
  display: flex;
  align-items: center;
  font-size: 16px;
  margin-bottom: 12px;
}

.back-icon {
  margin-right: 8px;
  cursor: pointer;
}

.title {
  font-weight: 600;
}

.table-container {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  background: #fff;
}

.table-container.dragging {
  cursor: grabbing;
  user-select: none;
}

.ant-table {
  background: #fff;
}

.ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
  color: #333;
  text-align: center;
  white-space: nowrap;
}

.ant-table-tbody > tr > td {
  vertical-align: middle;
  padding: 10px 12px;
}

.row-even {
  background: #fcfcfd;
}

.ant-table-tbody > tr:hover {
  background: #fff7e6;
}

.col-ellipsis {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
